// OpenAI API Service for Dynamic AI Responses

const OPENAI_API_KEY = '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';
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

// AI Persona System Prompts
const PERSONA_PROMPTS = {
  dr_sarah: {
    name: "Dr. Sarah Chen",
    title: "CBT Expert",
    systemPrompt: `You are Dr. Sarah Chen, a licensed clinical psychologist specializing in Cognitive Behavioral Therapy. Your approach is:

- Evidence-based and research-driven
- Pragmatic and solution-focused
- Often cite studies and statistics
- Advocate for CBT techniques
- Believe in structured, measurable interventions
- Use data to support your arguments
- Focus on practical tools clients can use immediately

Respond naturally in a professional but approachable tone. Keep responses conversational and under 150 words.`
  },
  dr_james: {
    name: "Dr. James Williams", 
    title: "Holistic Healer",
    systemPrompt: `You are Dr. James Williams, a holistic therapist who integrates traditional Western psychology with Eastern mindfulness practices. Your approach is:

- Emphasizes mind-body-spirit connection
- Calm and reflective communication style
- References meditation, mindfulness, and somatic healing
- Incorporates Eastern philosophy and practices
- Believes in treating the whole person
- Focuses on natural healing and awareness
- Uses gentle, compassionate language

Respond naturally in a calm, reflective tone. Keep responses conversational and under 150 words.`
  },
  dr_maria: {
    name: "Dr. Maria Rodriguez",
    title: "Analytical Psychologist", 
    systemPrompt: `You are Dr. Maria Rodriguez, a depth psychologist with expertise in analytical psychology and trauma-informed care. Your approach is:

- Focuses on understanding root causes
- Empathetic but probing questioning style
- Explores childhood and past experiences
- Advocates for long-term therapeutic relationships
- Believes in processing underlying trauma
- Uses insight-oriented approaches
- Values deep psychological understanding

Respond naturally in an empathetic but insightful tone. Keep responses conversational and under 150 words.`
  }
};

export const generateAIResponse = async (userMessage, currentTopic, conversationHistory, respondingDoctor) => {
  try {
    const persona = PERSONA_PROMPTS[respondingDoctor];
    if (!persona) {
      throw new Error(`Unknown doctor: ${respondingDoctor}`);
    }

    // Build conversation context
    const topicContext = getTopicContext(currentTopic);
    const recentHistory = conversationHistory.slice(-4); // Last 4 messages for context
    
    const systemPrompt = `${persona.systemPrompt}

Current Discussion Topic: ${topicContext.title}
Topic Focus: ${topicContext.description}

You are ${persona.name}, and you're in a live discussion about this topic. The user has just asked you a question.

IMPORTANT: 
- Answer the user's question directly and specifically
- Stay in character as ${persona.name}
- Keep your response conversational and under 100 words
- If they ask for your name, say "I'm ${persona.name}, ${persona.title}"
- Be helpful and professional`;

    const messages = [
      {
        role: "system",
        content: systemPrompt
      },
      {
        role: "user", 
        content: userMessage
      }
    ];

    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-3.5-turbo",
        messages: messages,
        max_tokens: 200,
        temperature: 0.8,
        presence_penalty: 0.1,
        frequency_penalty: 0.1
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content.trim();

  } catch (error) {
    console.error('Error generating AI response:', error);
    return getFallbackResponse(respondingDoctor, userMessage);
  }
};

const getTopicContext = (topicId) => {
  const topics = {
    anxiety_treatment: {
      title: "Best Approaches for Treating Anxiety",
      description: "Comparing different therapeutic interventions for anxiety disorders"
    },
    digital_vs_traditional: {
      title: "Digital Therapy vs Traditional Sessions", 
      description: "The role of technology in mental health care delivery"
    },
    work_life_balance: {
      title: "Work-Life Balance in Modern Times",
      description: "Strategies for preventing burnout and maintaining mental wellness"
    },
    depression_treatment: {
      title: "Depression Treatment",
      description: "Therapy vs medication approaches for treating depression"
    },
    child_psychology: {
      title: "Child Psychology", 
      description: "Early intervention strategies for childhood mental health"
    },
    sleep_disorders: {
      title: "Sleep Disorders",
      description: "CBT-I vs sleep medication approaches"
    }
  };
  
  return topics[topicId] || topics.anxiety_treatment;
};

const getFallbackResponse = (doctor, userMessage) => {
  const fallbacks = {
    dr_sarah: "That's an excellent question. From a CBT perspective, I'd recommend examining the thought patterns and behaviors that might be contributing to this issue. Have you considered keeping a thought record to identify any cognitive distortions?",
    dr_james: "I appreciate you sharing that with us. From a holistic standpoint, I believe we need to consider not just the mental aspect, but also how your body and spirit are responding. What does your body tell you about this situation?",
    dr_maria: "That's a thoughtful question that touches on deeper themes. I'm curious about what experiences in your past might be influencing how you're approaching this current situation. What comes to mind?"
  };
  
  return fallbacks[doctor] || "Thank you for that insightful question. I'd like to explore this further with you.";
};

export const selectRespondingDoctor = (userMessage, conversationHistory) => {
  const message = userMessage.toLowerCase();
  
  // Handle simple greetings and name questions - Dr. Sarah responds first
  if (message.includes('hi') || message.includes('hello') || message.includes('hey') || 
      message.includes('name') || message.includes('who are you')) {
    return 'dr_sarah';
  }
  
  // Route based on specific therapeutic approaches
  if (message.includes('cbt') || message.includes('cognitive') || message.includes('behavior') || 
      message.includes('evidence') || message.includes('technique') || message.includes('exercise')) {
    return 'dr_sarah';
  }
  
  if (message.includes('mindfulness') || message.includes('meditation') || message.includes('holistic') || 
      message.includes('body') || message.includes('breath') || message.includes('calm')) {
    return 'dr_james';
  }
  
  if (message.includes('trauma') || message.includes('childhood') || message.includes('past') || 
      message.includes('root') || message.includes('experience') || message.includes('feel')) {
    return 'dr_maria';
  }
  
  // For general questions, rotate between doctors
  const aiMessages = conversationHistory.filter(msg => msg.speaker !== 'user');
  const doctors = ['dr_sarah', 'dr_james', 'dr_maria'];
  return doctors[aiMessages.length % 3];
};

export const isOpenAIAvailable = () => {
  return !!OPENAI_API_KEY && OPENAI_API_KEY.length > 0;
};
